import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect } from 'react';

import { GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY } from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import TreatmentGroupRepository from '@/repositories/TreatmentGroupRepository';
import PaginatedResponse from '@/types/paginatedResponse';
import RepositoryResponse from '@/types/repositoryResponse';
import { TreatmentGroupForPerson } from '@/types/treatmentGroup';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

export interface TreatmentGroupsForPersonParams {
  // Required when filtering by is_eligible (needed to evaluate eligibility)
  // but not for a plain is_enrolled lookup.
  appointmentTypeId?: string;
  search?: string;
  timeZone?: string;
}

// The only person-scoped filters the for_person endpoint accepts. is_eligible
// is a single bucket covering the whole "fully eligible" vs "not" split --
// not eligible, not credentialed, and conflict-of-interest are all folded
// into is_eligible=false server-side, so the frontend never has to compose
// them itself.
export interface TreatmentGroupsForPersonFilters {
  is_eligible?: 'true' | 'false';
  is_enrolled?: 'true' | 'false';
}

type GroupsQueryResult = UseInfiniteQueryResult<
  InfiniteData<RepositoryResponse<PaginatedResponse<TreatmentGroupForPerson[]>>>
>;

const ELIGIBLE_PAGE_SIZE = 100;
const INELIGIBLE_PAGE_SIZE = 50;

const useGroupsQuery = (
  key: string,
  personId: string,
  params: TreatmentGroupsForPersonParams,
  filters: TreatmentGroupsForPersonFilters,
  pageSize: number,
  enabled: boolean
): GroupsQueryResult => {
  const { addMessage } = useContext(AppContext);
  const treatmentGroupRepository = TreatmentGroupRepository();

  const queryResponse = useInfiniteQuery({
    queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY, key, personId, params],
    queryFn: async ({ pageParam }: { pageParam: number }) =>
      treatmentGroupRepository.listForPerson(personId, {
        ...(params.appointmentTypeId && { appointment_type: params.appointmentTypeId }),
        is_draft: 'false',
        is_ended: 'false',
        ordering: 'name',
        page: String(pageParam),
        size: String(pageSize),
        ...(params.search && { search: params.search }),
        ...(params.timeZone && { timezone: params.timeZone }),
        ...filters
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.data?.links?.next ?? undefined,
    refetchOnWindowFocus: false,
    enabled: enabled && !!personId
  });

  const { error, isError } = queryResponse;
  useEffect(() => {
    if (isError) addMessage(mapAxiosErrorMessages(error as AxiosError));
  }, [addMessage, error, isError]);

  return queryResponse;
};

// Flattens one infinite query's pages into a single items list.
const flattenGroupsQuery = (query: GroupsQueryResult) => ({
  items: query.data?.pages.flatMap((page) => page.data?.results ?? []) ?? [],
  hasNextPage: query.hasNextPage,
  isFetchingNextPage: query.isFetchingNextPage,
  isPending: query.isLoading,
  isError: query.isError,
  fetchNextPage: query.fetchNextPage
});

// Always fetched in full -- currently-enrolled groups, regardless of current
// eligibility (matching the standing "enrolled always wins" rule). Split out
// from useEligibleTreatmentGroupsForPerson so each section paginates
// independently instead of sharing one combined fetch sequence.
export const useEnrolledTreatmentGroupsForPerson = (
  personId: string,
  params: TreatmentGroupsForPersonParams
) => {
  const enrolled = useGroupsQuery(
    'enrolled',
    personId,
    params,
    { is_enrolled: 'true' },
    ELIGIBLE_PAGE_SIZE,
    true
  );

  return flattenGroupsQuery(enrolled);
};

// Always fetched in full -- groups the person is fully eligible for and not
// already enrolled in.
export const useEligibleTreatmentGroupsForPerson = (
  personId: string,
  params: TreatmentGroupsForPersonParams
) => {
  const eligible = useGroupsQuery(
    'eligible',
    personId,
    params,
    { is_enrolled: 'false', is_eligible: 'true' },
    ELIGIBLE_PAGE_SIZE,
    true
  );

  return flattenGroupsQuery(eligible);
};

// Only fetched once `enabled` (the "Not eligible" section has been expanded).
// is_eligible=false already covers every way a group can fail to be fully
// eligible, so this is a single request rather than one per failure mode.
export const useIneligibleTreatmentGroupsForPerson = (
  personId: string,
  params: TreatmentGroupsForPersonParams,
  enabled: boolean
) => {
  const ineligible = useGroupsQuery(
    'ineligible',
    personId,
    params,
    { is_enrolled: 'false', is_eligible: 'false' },
    INELIGIBLE_PAGE_SIZE,
    enabled
  );

  return flattenGroupsQuery(ineligible);
};
